import { isTruthy } from '@ardoq/common-helpers';
import { toWorkspaceOption } from '@ardoq/perspectives';
import {
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  PerspectivesGroupsTags,
  PerspectivesGroupsWorkspace,
  TagGroupingRule,
} from '@ardoq/perspectives';
import { Select } from '@ardoq/select';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ArdoqId } from '@ardoq/api-types';
import { RowHeader } from '../../atoms';

type TagGroupRuleProps = {
  groupingRule: TagGroupingRule;
  workspaces: PerspectivesGroupsWorkspace[];
  tagsPerWorkspace: Record<ArdoqId, PerspectivesGroupsTags[]>;
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  tabIndex?: number;
  i18nLocale?: I18nLocale;
};

const toTagOption = (tag: PerspectivesGroupsTags) => ({
  value: tag.id,
  label: tag.name,
});

const TagGroupRule = ({
  tabIndex,
  workspaces,
  groupingRule,
  tagsPerWorkspace,
  onGroupingRuleUpdated,
  getInvalidGroupingSelectOptions,
  i18nLocale = {},
}: TagGroupRuleProps) => {
  const { workspaceSelectOption, ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const workspaceOptions = [
    workspaceSelectOption,
    ...workspaces.map(workspace => toWorkspaceOption(workspace)),
  ]
    .filter(isTruthy)
    .map(option => ({
      ...option,
      ...(!tagsPerWorkspace[option.value]?.length
        ? {
            isDisabled: true,
            popoverContent: t(
              i18nLocale,
              'perspectivesSidebar:groupingRules.noTagsInWorkspace',
              'There are no tags in this workspace for the opened datasets'
            ),
          }
        : {}),
    }));

  const tags = tagsPerWorkspace[groupingRule.workspaceId!] ?? [];
  const tagOptions = [ruleTargetSelectOption, ...tags.map(toTagOption)].filter(
    isTruthy
  );

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <RowHeader>
        {t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.inWorkspace',
          'In workspace'
        )}
      </RowHeader>
      <Select
        value={groupingRule.workspaceId}
        options={workspaceOptions}
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.workspaceAriaLabel',
          'Workspace'
        )}
        dataTestId="tag-grouping-rule-workspace-select"
        tabIndex={tabIndex}
        onChange={event => {
          const nextWorkspaceId = event?.value;
          if (!nextWorkspaceId) return;
          const defaultTagId = tagsPerWorkspace[nextWorkspaceId]?.[0]?.id;
          updateGroupRule({
            workspaceId: nextWorkspaceId,
            targetId: defaultTagId,
          });
        }}
        menuPortalTarget={null}
      />
      <RowHeader>
        {t(i18nLocale, 'perspectivesSidebar:groupingRules.type', 'Type')}
      </RowHeader>
      <Select
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:groupingRules.tagsAriaLabel',
          'Tags'
        )}
        options={tagOptions}
        dataTestId="tag-grouping-rule-tag-select"
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            targetId: event?.value,
          })
        }
        menuPortalTarget={null}
      />
    </>
  );
};

export { TagGroupRule };
